/**
 * Demo Service
 * Provides utility functions for DOM manipulation, element targeting, and positioning
 * Used by demo components for highlighting, scrolling, and tooltip positioning
 */

import { TOOLTIP, OVERLAY, ANIMATION, DEMO_BEHAVIOR, BREAKPOINTS } from '@/demo/constants';

/**
 * Get reference to target element by CSS selector
 * @param {string} selector - CSS selector string (supports multiple selectors with comma)
 * @returns {HTMLElement|null} - Target element or null if not found
 */
export function getTargetElement(selector) {
  if (!selector || typeof selector !== 'string') {
    console.warn('[DemoService] Invalid selector provided:', selector);
    return null;
  }

  // Handle multiple selectors separated by comma
  const selectors = selector.split(',').map(s => s.trim());

  for (const sel of selectors) {
    try {
      // First try main document
      const element = document.querySelector(sel);
      if (element) {
        return element;
      }

      // If not found in main document, try searching inside iframes
      const iframes = document.querySelectorAll('iframe');
      for (const iframe of iframes) {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc) {
            const iframeElement = iframeDoc.querySelector(sel);
            if (iframeElement) {
              return iframeElement;
            }
          }
        } catch (iframeError) {
          // Skip iframes we can't access (CORS)
          continue;
        }
      }
    } catch (error) {
      console.warn(`[DemoService] Invalid selector: "${sel}"`, error);
    }
  }

  console.warn('[DemoService] No element found for selector:', selector);
  return null;
}

/**
 * Wait for element to appear in DOM
 * Hybrid approach: polling + MutationObserver for reliable API-loaded content detection
 * @param {string} selector - CSS selector for target element
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<HTMLElement>} - Resolves with element when found
 */
export function waitForElement(selector, timeout = DEMO_BEHAVIOR.ELEMENT_WAIT_TIMEOUT) {
  return new Promise((resolve, reject) => {
    // Check if element already exists
    const existingElement = getTargetElement(selector);
    if (existingElement) {
      console.log(`[DemoService] ✅ Element found immediately: ${selector}`);
      resolve(existingElement);
      return;
    }

    let settled = false;

    const finish = (el, source = '') => {
      if (settled) return;
      settled = true;
      clearInterval(pollId);
      observer.disconnect();
      clearTimeout(timeoutId);
      
      if (el) {
        console.log(`[DemoService] ✅ Element found (${source}): ${selector}`);
        resolve(el);
      } else {
        console.warn(`[DemoService] ❌ Element not found within ${timeout}ms: ${selector}`);
        reject(new Error(`Element not found within ${timeout}ms: ${selector}`));
      }
    };

    // POLLING — catches missed mutations and handles Vue batching
    // Run every 100ms to reliably detect API-loaded elements
    const pollId = setInterval(() => {
      const element = getTargetElement(selector);
      if (element) {
        finish(element, 'poll');
      }
    }, 100);

    // MutationObserver — fast-path for immediate DOM changes
    const observer = new MutationObserver(() => {
      const element = getTargetElement(selector);
      if (element) {
        finish(element, 'mutation');
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,  // Watch attribute changes for v-show, hidden, style, class
      attributeFilter: ['style', 'class', 'hidden', 'data-v-show']
    });

    // Timeout — give up after specified duration
    const timeoutId = setTimeout(() => {
      finish(null, 'timeout');
    }, timeout);
  });
}

/**
 * Check if element is visible in viewport
 * @param {HTMLElement} element - Target element
 * @param {boolean} partiallyVisible - If true, returns true if any part is visible
 * @returns {boolean} - True if element is (partially) visible
 */
export function isElementVisible(element, partiallyVisible = true) {
  if (!element) return false;

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
    return false;
  }

  const rect = getAbsoluteBounds(element);
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const vertInView = partiallyVisible
    ? rect.bottom > 0 && rect.top < windowHeight
    : rect.top >= 0 && rect.bottom <= windowHeight;

  const horInView = partiallyVisible
    ? rect.right > 0 && rect.left < windowWidth
    : rect.left >= 0 && rect.right <= windowWidth;

  return vertInView && horInView;
}

/**
 * Scroll element into view with smooth animation
 * @param {HTMLElement} element - Target element to scroll to
 * @param {number} padding - Extra padding from viewport edge (pixels)
 * @returns {Promise<void>} - Resolves when scroll completes
 */
export function scrollToElement(element, padding = DEMO_BEHAVIOR.SCROLL_PADDING) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    // Check if already visible
    if (isElementVisible(element)) {
      resolve();
      return;
    }

    // For elements in iframes, try to scroll the iframe itself into view first
    let ownerDoc = element.ownerDocument;
    if (ownerDoc !== document) {
      const iframes = document.querySelectorAll('iframe');
      for (const iframe of iframes) {
        try {
          if (iframe.contentDocument === ownerDoc) {
            // Scroll the iframe into view
            iframe.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            break;
          }
        } catch (error) {
          // Can't access iframe
        }
      }
    }

    // Now scroll the element within its document
    const rect = element.getBoundingClientRect();
    const absoluteElementTop = rect.top + window.pageYOffset;
    const middle = absoluteElementTop - padding;

    window.scrollTo({
      top: middle,
      behavior: 'smooth',
    });

    // Resolve after scroll animation
    setTimeout(resolve, ANIMATION.DURATION);
  });
}
/**
 * Get absolute bounding rectangle for element, accounting for iframe nesting
 * @param {HTMLElement} element - Target element (may be inside an iframe)
 * @returns {Object} - { top, left, width, height, bottom, right }
 */
function getAbsoluteBounds(element) {
  if (!element) return null;

  let rect = element.getBoundingClientRect();
  let top = rect.top;
  let left = rect.left;

  // Check if element is inside an iframe by traversing up the DOM
  let currentElement = element;
  let ownerDoc = element.ownerDocument;

  while (ownerDoc !== document) {
    // Found that element is in an iframe, get the iframe's position
    const iframes = document.querySelectorAll('iframe');
    for (const iframe of iframes) {
      try {
        if (iframe.contentDocument === ownerDoc) {
          // This is the iframe containing our element
          const iframeRect = iframe.getBoundingClientRect();
          top += iframeRect.top;
          left += iframeRect.left;

          // Continue checking if the iframe itself is nested in another iframe
          ownerDoc = iframe.ownerDocument;
          continue; // Check again for nested iframes
        }
      } catch (error) {
        // Can't access iframe (CORS)
        break;
      }
    }
    // If we couldn't find the iframe, we're done
    break;
  }

  return {
    top,
    left,
    width: rect.width,
    height: rect.height,
    bottom: top + rect.height,
    right: left + rect.width,
  };
}

/**
 * Get bounding rectangle for element with padding
 * @param {HTMLElement} element - Target element
 * @param {number} padding - Padding around element (pixels)
 * @returns {Object} - { top, left, width, height, bottom, right }
 */
export function getElementBoundsWithPadding(element, padding = 0) {
  if (!element) return null;

  const rect = getAbsoluteBounds(element);

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    bottom: rect.bottom + padding,
    right: rect.right + padding,
  };
}

/**
 * Calculate tooltip position based on target element and preferred position
 * @param {HTMLElement} element - Target element reference
 * @param {string} preferredPosition - Preferred position (top, bottom, left, right, auto)
 * @returns {Object} - { position, top, left, arrowPosition }
 */
export function calculateTooltipPosition(element, preferredPosition = 'bottom') {
  if (!element) return { position: 'bottom', top: 0, left: 0 };

  const rect = getAbsoluteBounds(element);
  const tooltipWidth = TOOLTIP.MAX_WIDTH;
  const tooltipHeight = 160; // Approximate height
  const offset = TOOLTIP.OFFSET;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  // Reserve space at the bottom for the demo controls bar
  const CONTROLS_RESERVE = 130;

  // Note: rect values are viewport-relative (from getBoundingClientRect)
  // Since tooltip uses position: fixed, coordinates are viewport-relative
  // Do NOT add scroll offsets
  
  // Calculate positions for each direction
  const positions = {
    top: {
      top: rect.top - tooltipHeight - offset,
      left: rect.left + rect.width / 2 - tooltipWidth / 2,
      position: 'top',
    },
    bottom: {
      top: rect.top + rect.height + offset,
      left: rect.left + rect.width / 2 - tooltipWidth / 2,
      position: 'bottom',
    },
    left: {
      top: rect.top + rect.height / 2,
      left: rect.left - tooltipWidth - offset,
      position: 'left',
      verticalCenter: true,
    },
    right: {
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width + offset,
      position: 'right',
      verticalCenter: true,
    },
  };

  // If preferred position is 'auto', choose best fit
  if (preferredPosition === 'auto') {
    // Check available space and choose best position
    const spaceTop = rect.top;
    const spaceBottom = windowHeight - rect.bottom - CONTROLS_RESERVE;
    const spaceLeft = rect.left;
    const spaceRight = windowWidth - rect.right;

    if (spaceBottom > tooltipHeight + offset) {
      return positions.bottom;
    } else if (spaceTop > tooltipHeight + offset) {
      return positions.top;
    } else if (spaceRight > tooltipWidth + offset) {
      return positions.right;
    } else if (spaceLeft > tooltipWidth + offset) {
      return positions.left;
    } else {
      return positions.bottom; // Fallback
    }
  }

  // Use preferred position, with fallback if not enough space
  const selectedPosition = positions[preferredPosition];
  if (!selectedPosition) return positions.bottom;

  // Validate position doesn't exceed viewport bounds
  const adjustedPosition = { ...selectedPosition };

  // Check horizontal bounds (viewport-relative)
  if (adjustedPosition.left < 0) {
    adjustedPosition.left = 10; // Small margin from left edge
  } else if (adjustedPosition.left + tooltipWidth > windowWidth) {
    adjustedPosition.left = windowWidth - tooltipWidth - 10; // Small margin from right edge
  }

  // Check vertical bounds (viewport-relative), reserve space for the demo controls bar
  if (adjustedPosition.top < 0) {
    adjustedPosition.top = 10;
  } else if (adjustedPosition.top + tooltipHeight > windowHeight - CONTROLS_RESERVE) {
    adjustedPosition.top = windowHeight - CONTROLS_RESERVE - tooltipHeight - 10;
  }

  return adjustedPosition;
}

/**
 * Create and apply highlight box around element
 * @param {HTMLElement} element - Element to highlight
 * @param {number} padding - Padding around element
 * @returns {HTMLElement} - Highlight element created
 */
export function highlightElement(element, padding = OVERLAY.HIGHLIGHT_PADDING || 8) {
  if (!element) return null;

  // Remove existing highlight if any
  removeHighlight();

  const bounds = getElementBoundsWithPadding(element, padding);
  const highlight = document.createElement('div');

  highlight.id = 'demo-highlight-box';
  highlight.style.cssText = `
    position: fixed;
    top: ${bounds.top}px;
    left: ${bounds.left}px;
    width: ${bounds.width}px;
    height: ${bounds.height}px;
    border: ${OVERLAY.HIGHLIGHT_WIDTH || 3}px ${OVERLAY.HIGHLIGHT_STYLE || 'solid'} ${OVERLAY.HIGHLIGHT_COLOR || '#FFD700'};
    border-radius: 4px;
    box-shadow: ${OVERLAY.HIGHLIGHT_BOX_SHADOW || '0 0 20px rgba(255, 215, 0, 0.6)'};
    pointer-events: none;
    z-index: ${OVERLAY.HIGHLIGHT_Z_INDEX || 998};
    animation: demo-highlight-pulse 2s ease-in-out infinite;
    transition: all ${ANIMATION.DURATION}ms ${ANIMATION.EASING};
  `;

  document.body.appendChild(highlight);
  return highlight;
}

/**
 * Update highlight position (e.g., when window resizes or element moves)
 * @param {HTMLElement} element - Element to track
 * @param {number} padding - Padding around element
 */
export function updateHighlight(element, padding = 8) {
  const highlight = document.getElementById('demo-highlight-box');
  if (!highlight || !element) return;

  const bounds = getElementBoundsWithPadding(element, padding);

  highlight.style.top = `${bounds.top}px`;
  highlight.style.left = `${bounds.left}px`;
  highlight.style.width = `${bounds.width}px`;
  highlight.style.height = `${bounds.height}px`;
}

/**
 * Remove highlight box
 */
export function removeHighlight() {
  const highlight = document.getElementById('demo-highlight-box');
  if (highlight) {
    highlight.style.opacity = '0';
    setTimeout(() => {
      if (highlight.parentNode) {
        highlight.parentNode.removeChild(highlight);
      }
    }, ANIMATION.FADE_DURATION);
  }
}

/**
 * Create semi-transparent backdrop overlay
 * @returns {HTMLElement} - Backdrop element created
 */
export function createBackdrop() {
  removeBackdrop();

  const backdrop = document.createElement('div');
  backdrop.id = 'demo-backdrop';
  backdrop.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${OVERLAY.BACKDROP_COLOR || 'rgba(0, 0, 0, 0.5)'};
    z-index: ${OVERLAY.BACKDROP_Z_INDEX || 998};
    pointer-events: auto;
    opacity: 0;
    animation: demo-fade-in ${ANIMATION.FADE_DURATION}ms ${ANIMATION.EASING} forwards;
  `;

  document.body.appendChild(backdrop);
  return backdrop;
}

/**
 * Remove backdrop overlay
 */
export function removeBackdrop() {
  const backdrop = document.getElementById('demo-backdrop');
  if (backdrop) {
    backdrop.style.opacity = '0';
    setTimeout(() => {
      if (backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
    }, ANIMATION.FADE_DURATION);
  }
}

/**
 * Get current viewport dimensions
 * @returns {Object} - { width, height, scrollX, scrollY }
 */
export function getViewportDimensions() {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
    scrollX: window.pageXOffset || document.documentElement.scrollLeft,
    scrollY: window.pageYOffset || document.documentElement.scrollTop,
  };
}

/**
 * Determine if current view is mobile based on window width
 * @returns {boolean} - True if mobile viewport
 */
export function isMobileViewport() {
  return getViewportDimensions().width <= BREAKPOINTS.TABLET;
}

/**
 * Inject animation keyframes if not already present
 */
export function injectDemoAnimations() {
  // Check if styles already injected
  if (document.getElementById('demo-animations')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'demo-animations';
  style.innerHTML = `
    @keyframes demo-highlight-pulse {
      0% {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
      }
      50% {
        box-shadow: 0 0 30px rgba(255, 215, 0, 0.9);
      }
      100% {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
      }
    }

    @keyframes demo-fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes demo-fade-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @keyframes demo-slide-in {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes demo-slide-out {
      from {
        transform: translateY(0);
        opacity: 1;
      }
      to {
        transform: translateY(10px);
        opacity: 0;
      }
    }
  `;

  document.head.appendChild(style);
}

/**
 * Clean up all demo elements from DOM
 */
export function cleanupDemo() {
  removeHighlight();
  removeBackdrop();

  // Remove any remaining demo elements
  const demoElements = document.querySelectorAll('[id^="demo-"]');
  demoElements.forEach(el => {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  });
}

export default {
  getTargetElement,
  waitForElement,
  isElementVisible,
  scrollToElement,
  getElementBoundsWithPadding,
  calculateTooltipPosition,
  highlightElement,
  updateHighlight,
  removeHighlight,
  createBackdrop,
  removeBackdrop,
  getViewportDimensions,
  isMobileViewport,
  injectDemoAnimations,
  cleanupDemo,
};
